(() => {
  if (window.NewFrontierCollabGrid) return;

  const desktopMedia = window.matchMedia('(min-width: 768px)');
  const gridObservers = new WeakMap();
  const scheduledGrids = new WeakMap();

  function collect(scope, selector) {
    const elements = [];

    if (scope instanceof Element && scope.matches(selector)) elements.push(scope);
    if (scope?.querySelectorAll) elements.push(...scope.querySelectorAll(selector));

    return elements;
  }

  function groupProductsByRow(grid, mediaItem) {
    const mediaTop = mediaItem.getBoundingClientRect().top;
    const rowTolerance = 2;
    const rows = [];

    collect(grid, ':scope > .collection__grid-item').forEach((productItem) => {
      const productTop = productItem.getBoundingClientRect().top;

      if (productTop < mediaTop - rowTolerance) return;

      let row = rows.find((candidate) => Math.abs(candidate.top - productTop) <= rowTolerance);

      if (!row) {
        row = { top: productTop, products: [] };
        rows.push(row);
      }

      row.products.push(productItem);
    });

    return rows.sort((first, second) => first.top - second.top);
  }

  function syncMediaHeight(mediaItem) {
    if (!desktopMedia.matches) {
      mediaItem.style.removeProperty('--collab-grid-media-height');
      return;
    }

    const grid = mediaItem.closest('.collection__grid');

    if (!grid) return;

    const productRows = groupProductsByRow(grid, mediaItem);
    const secondProductRow = productRows[1];

    if (!secondProductRow) {
      mediaItem.style.removeProperty('--collab-grid-media-height');
      return;
    }

    const imageBottoms = secondProductRow.products
      .map((productItem) => productItem.querySelector('.product-card__media')?.getBoundingClientRect().bottom)
      .filter((bottom) => Number.isFinite(bottom));

    if (!imageBottoms.length) {
      mediaItem.style.removeProperty('--collab-grid-media-height');
      return;
    }

    const mediaTop = mediaItem.getBoundingClientRect().top;
    const targetHeight = Math.max(...imageBottoms) - mediaTop;

    if (targetHeight > 0) {
      mediaItem.style.setProperty('--collab-grid-media-height', `${targetHeight}px`);
    }
  }

  function syncGrid(grid) {
    collect(grid, ':scope > [data-collab-grid-media-item]').forEach(syncMediaHeight);
  }

  function scheduleGridSync(grid) {
    if (!grid || scheduledGrids.has(grid)) return;

    const frame = window.requestAnimationFrame(() => {
      scheduledGrids.delete(grid);
      syncGrid(grid);
    });

    scheduledGrids.set(grid, frame);
  }

  function observeGrid(grid) {
    if (gridObservers.has(grid)) {
      const observer = gridObservers.get(grid);

      collect(grid, '.product-card__media').forEach((media) => observer.observe(media));
      scheduleGridSync(grid);
      return;
    }

    const observer = new ResizeObserver(() => scheduleGridSync(grid));

    observer.observe(grid);
    collect(grid, '.product-card__media').forEach((media) => observer.observe(media));
    gridObservers.set(grid, observer);
    scheduleGridSync(grid);
  }

  function updateSoundButton(button, video) {
    const isAudible = !video.muted;

    button.setAttribute('aria-pressed', String(isAudible));
    button.setAttribute('aria-label', isAudible ? 'Mute video' : 'Unmute video');
    button.dataset.soundEnabled = String(isAudible);
  }

  function initialize(scope = document) {
    collect(scope, '[data-collab-grid-sound-toggle]').forEach((button) => {
      const shell = button.closest('.collab-grid__media-shell');
      const video = shell?.querySelector('video');

      if (video) updateSoundButton(button, video);
    });

    const grids = new Set(
      collect(scope, '[data-collab-grid-media-item]')
        .map((mediaItem) => mediaItem.closest('.collection__grid'))
        .filter(Boolean)
    );

    grids.forEach(observeGrid);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-collab-grid-sound-toggle]');

    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const shell = button.closest('.collab-grid__media-shell');
    const video = shell?.querySelector('video');

    if (!video) return;

    video.muted = !video.muted;
    updateSoundButton(button, video);

    if (video.paused) {
      video.play().catch(() => {});
    }
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('resize', () => {
    document.querySelectorAll('.collection__grid').forEach(scheduleGridSync);
  });

  desktopMedia.addEventListener('change', () => initialize());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  window.NewFrontierCollabGrid = { initialize };
})();
